<template>
  <div :class="rootClass" :style="rootStyle">
    <div class="Border-inner">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import type { StyleValue } from 'vue'

defineOptions({
  inheritAttrs: false
})

const props = defineProps<{
  radius?: string
  borderWidth?: string
  innerBg?: string
}>()

const attrs = useAttrs()

const rootStyle = computed(() => attrs.style as StyleValue | undefined)

const rootClass = computed(() => {
  const classes = ['Border', 'box-border', 'inline-block']
  if (attrs.class) {
    if (typeof attrs.class === 'string') {
      classes.push(attrs.class)
    } else if (Array.isArray(attrs.class)) {
      classes.push(...attrs.class.filter(Boolean) as string[])
    } else if (typeof attrs.class === 'object') {
      // 处理对象形式的 class
      Object.keys(attrs.class).forEach(key => {
        if (attrs.class[key]) {
          classes.push(key)
        }
      })
    }
  }
  return classes
})

const borderWidth = computed(() => props.borderWidth || '2px')
const radius = computed(() => props.radius)

// 解析多值字符串，返回数值和单位的数组
function parseMultiValue(value: string): Array<{ num: number; unit: string }> {
  const matches = value.match(/([\d.]+)(\w*)/g)
  if (!matches) return []

  return matches.map(match => {
    const parsed = match.match(/([\d.]+)(\w*)/)
    if (!parsed) return { num: 0, unit: 'px' }
    return {
      num: parseFloat(parsed[1]),
      unit: parsed[2] || 'px'
    }
  })
}

// 将多值数组展开为四个值（按照 CSS 规则：top-left, top-right, bottom-right, bottom-left）
function expandToFourValues(values: Array<{ num: number; unit: string }>): Array<{ num: number; unit: string }> {
  if (values.length === 0) return []
  if (values.length === 1) {
    // 一个值：应用到所有角
    return [values[0], values[0], values[0], values[0]]
  } else if (values.length === 2) {
    // 两个值：第一个应用于 top-left 和 bottom-right，第二个应用于 top-right 和 bottom-left
    return [values[0], values[1], values[0], values[1]]
  } else if (values.length === 3) {
    // 三个值：第一个应用于 top-left，第二个应用于 top-right 和 bottom-left，第三个应用于 bottom-right
    return [values[0], values[1], values[2], values[1]]
  } else {
    // 四个或更多值：按顺序应用于 top-left, top-right, bottom-right, bottom-left
    return values.slice(0, 4)
  }
}

// 将 border-width/padding 格式（top, right, bottom, left）转换为四个角的值
// 每个角的边框宽度取相邻两条边的平均值
function convertBorderWidthToCorners(values: Array<{ num: number; unit: string }>): Array<{ num: number; unit: string }> {
  if (values.length === 0) return []
  
  const unit = values[0]?.unit || 'px'
  
  if (values.length === 1) {
    // 一个值：所有边相同，所有角也相同
    return [values[0], values[0], values[0], values[0]]
  } else if (values.length === 2) {
    // 两个值：[top/bottom, left/right]
    // top-left = (top + left) / 2 = (values[0] + values[1]) / 2
    // top-right = (top + right) / 2 = (values[0] + values[1]) / 2
    // bottom-right = (bottom + right) / 2 = (values[0] + values[1]) / 2
    // bottom-left = (bottom + left) / 2 = (values[0] + values[1]) / 2
    const avg = { num: (values[0].num + values[1].num) / 2, unit }
    return [avg, avg, avg, avg]
  } else if (values.length === 3) {
    // 三个值：[top, left/right, bottom]
    // top-left = (top + left) / 2 = (values[0] + values[1]) / 2
    // top-right = (top + right) / 2 = (values[0] + values[1]) / 2
    // bottom-right = (bottom + right) / 2 = (values[2] + values[1]) / 2
    // bottom-left = (bottom + left) / 2 = (values[2] + values[1]) / 2
    const topAvg = { num: (values[0].num + values[1].num) / 2, unit }
    const bottomAvg = { num: (values[2].num + values[1].num) / 2, unit }
    return [topAvg, topAvg, bottomAvg, bottomAvg]
  } else {
    // 四个值：[top, right, bottom, left]
    // top-left = (top + left) / 2 = (values[0] + values[3]) / 2
    // top-right = (top + right) / 2 = (values[0] + values[1]) / 2
    // bottom-right = (bottom + right) / 2 = (values[2] + values[1]) / 2
    // bottom-left = (bottom + left) / 2 = (values[2] + values[3]) / 2
    return [
      { num: (values[0].num + values[3].num) / 2, unit }, // top-left
      { num: (values[0].num + values[1].num) / 2, unit }, // top-right
      { num: (values[2].num + values[1].num) / 2, unit }, // bottom-right
      { num: (values[2].num + values[3].num) / 2, unit }  // bottom-left
    ]
  }
}

// 计算内层圆角，支持多值格式如 "10px 10px 0px 0px" 和 "3px 2px 0px 2px"
const innerRadius = computed(() => {
  if (!props.radius) return undefined

  // 解析 borderWidth 和 radius
  const widthValues = parseMultiValue(borderWidth.value)
  const radiusValues = parseMultiValue(props.radius)

  if (widthValues.length === 0 || radiusValues.length === 0) return undefined

  // radius 按照四个角的顺序展开：top-left, top-right, bottom-right, bottom-left
  const expandedRadii = expandToFourValues(radiusValues)
  
  // borderWidth 按照 border-width/padding 顺序（top, right, bottom, left）转换为四个角的值
  const expandedWidths = convertBorderWidthToCorners(widthValues)

  // 获取单位（优先使用 radius 的单位）
  const unit = expandedRadii[0]?.unit || expandedWidths[0]?.unit || 'px'

  // 对每个圆角值减去对应的边框宽度
  const innerValues = expandedRadii.map((radius, index) => {
    const width = expandedWidths[index]?.num || 0
    const innerValue = Math.max(0, radius.num - width)
    return `${innerValue}${unit}`
  })

  return innerValues.join(' ')
})
</script>

<style scoped>
.Border {
  width: 100%;
  box-shadow: 0px 2px 1px 0px #3D3200;
  position: relative;
  background: transparent;
  border-radius: v-bind(radius);
  padding: v-bind(borderWidth);
}

/* 使用伪元素承载渐变，并通过 mask 只在 padding 区域显示 */
.Border::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: inherit;
  background: linear-gradient(180deg, #FFBF2F, #E6C32C);
  /* mask 让渐变只在 padding 区域显示，中间部分透明 */
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: 0;
}

.Border-inner {
  position: relative;
  z-index: 1;
  min-height: 0;
  border-radius: v-bind(innerRadius);
  background: v-bind(innerBg);
  width: 100%;
  height: 100%;

}
</style>
